import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheckCircle, faExclamationCircle } from '@/icons';
import { config } from '../config/environment';

/**
 * Unsubscribe page component
 * Handles email unsubscribe requests (for transactional emails, this is informational only)
 */
export const UnsubscribePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    const [email, setEmail] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string>('');

    // Get email from query parameter if present
    useEffect(() => {
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(decodeURIComponent(emailParam));
        }
    }, [searchParams]);

    const handleUnsubscribe = async () => {
        if (!email || !email.trim()) {
            setError('Please enter your email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Use fetch directly since this is a public endpoint
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to process unsubscribe request' }));
                throw new Error(errorData.message || 'Failed to process unsubscribe request');
            }

            const data = await response.json();
            setIsSuccess(true);
            setMessage(data.message);
        } catch (err: any) {
            setError(err.message || 'Failed to process unsubscribe request. Please try again or contact support.');
        } finally {
            setIsProcessing(false);
        }
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.lg,
        },
        card: {
            width: '100%',
            maxWidth: '500px',
            margin: '0 auto',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.xl,
            boxShadow: theme.shadows.lg,
            border: `1px solid ${theme.colors.border.default}`,
        },
        header: {
            textAlign: 'center' as const,
            marginBottom: theme.spacing.xl,
        },
        icon: {
            fontSize: '48px',
            color: theme.colors.primary,
            marginBottom: theme.spacing.md,
        },
        title: {
            ...themedStyles.text.heading,
            fontSize: theme.typography.fontSize.xl,
            marginBottom: theme.spacing.sm,
        },
        subtitle: {
            ...themedStyles.text.body,
            color: theme.colors.text.secondary,
            fontSize: theme.typography.fontSize.sm,
        },
        form: {
            marginBottom: theme.spacing.lg,
        },
        input: {
            width: '100%',
            padding: `${theme.spacing.md} ${theme.spacing.lg}`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.md,
            backgroundColor: theme.colors.background,
            color: theme.colors.text.primary,
            marginBottom: theme.spacing.md,
            boxSizing: 'border-box' as const,
        },
        button: {
            width: '100%',
            padding: `${theme.spacing.md} ${theme.spacing.lg}`,
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.inverse,
            border: 'none',
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            transition: `background-color ${theme.transitions.normal}`,
            opacity: isProcessing ? 0.6 : 1,
        },
        successMessage: {
            padding: theme.spacing.lg,
            backgroundColor: '#d4edda',
            border: `1px solid #c3e6cb`,
            borderRadius: theme.borderRadius.md,
            color: '#155724',
            marginBottom: theme.spacing.md,
        },
        errorMessage: {
            padding: theme.spacing.lg,
            backgroundColor: '#f8d7da',
            border: `1px solid #f5c6cb`,
            borderRadius: theme.borderRadius.md,
            color: '#721c24',
            marginBottom: theme.spacing.md,
        },
        infoBox: {
            padding: theme.spacing.md,
            backgroundColor: theme.colors.surfaceSecondary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.md,
            marginTop: theme.spacing.lg,
        },
        infoText: {
            ...themedStyles.text.body,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.secondary,
            lineHeight: 1.6,
        },
        link: {
            color: theme.colors.primary,
            textDecoration: 'none',
            cursor: 'pointer',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <FontAwesomeIcon icon={faEnvelope} style={styles.icon} />
                    <h1 style={styles.title}>Unsubscribe from Emails</h1>
                    <p style={styles.subtitle}>
                        Manage your email preferences
                    </p>
                </div>

                {isSuccess ? (
                    <div>
                        <div style={styles.successMessage}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <strong>Request Received</strong>
                            </div>
                            <p style={{ margin: 0 }}>{message}</p>
                        </div>
                        <button
                            onClick={() => navigate('/auth/login')}
                            style={styles.button}
                        >
                            Return to Login
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={styles.form}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                disabled={isProcessing}
                                style={styles.input}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !isProcessing) {
                                        handleUnsubscribe();
                                    }
                                }}
                            />
                            <button
                                onClick={handleUnsubscribe}
                                disabled={isProcessing}
                                style={styles.button}
                            >
                                {isProcessing ? 'Processing...' : 'Unsubscribe'}
                            </button>
                        </div>

                        {error && (
                            <div style={styles.errorMessage}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                                    <FontAwesomeIcon icon={faExclamationCircle} />
                                    <strong>Error</strong>
                                </div>
                                <p style={{ margin: 0 }}>{error}</p>
                            </div>
                        )}

                        <div style={styles.infoBox}>
                            <p style={styles.infoText}>
                                <strong>Note:</strong> Calculist sends transactional emails such as password reset requests and team invitations.
                                These emails are essential for account management and cannot be unsubscribed.
                                If you have concerns about receiving these emails, please{' '}
                                <a href="mailto:support@calculist.app" style={styles.link}>
                                    contact support
                                </a>.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
